import { useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  Info,
  LogOut,
  Crown,
  Settings,
  Headset,
  Sparkles,
  Moon,
  Sun,
  Palette,
  Globe,
  Monitor,
  ChevronDown,
  TriangleAlert,
  Gauge,
  UserCog,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  // DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';
import { Sheet, SheetContent } from 'components/ui/sheet';
import { useSignoutMutation } from 'features/auth/hooks/use-auth';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useNavigate, useLocation } from 'react-router-dom';
import DummyProfile from 'assets/images/dummy_profile.png';
import { UProfileItem } from '/pages/main/uprofile/types/uprofile-type';
import { ArchiveChat } from 'pages/archive/archive-chat';
import { useTranslation } from 'react-i18next';
import { getLanguageMenuLabel } from 'components/blocks/language-selector/language-selector';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useQueryClient } from '@tanstack/react-query';
import { completeLogout } from 'features/auth/utils/complete-logout';
import { useFreeAccess, useMyPlan } from 'features/subscription/hooks/use-seats';
import { useSettingsDialogStore } from 'state/store/settings-dialog/useSettingsDialogStore';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { SettingsDialog } from '../settings-dialog';
import seliseLogo from 'assets/images/selise-ai-logo.svg';
import { KnowledgeBaseModal } from 'features/profile/components/knowledge-base-modal';
import { PLANS } from 'constant/plan';
import ContactModal from 'features/subscription/components/contact-sales-modal';
import { useContactSales } from 'features/subscription/hooks/use-subscription';
import { useConfig } from 'hooks/use-config';
import { useLanguageContext } from 'i18n/language-context';
import { useSecureConfigStore } from 'state/store/config/useConfigStore';
import { useTheme } from 'components/core/theme-provider';
import { updateCurrentLanguage } from '../language-selector/services/language-service';
import { useIsMobile } from 'hooks/use-mobile';
import { cn, formatDate } from 'lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { TruncateTooltip } from 'hooks/use-truncate-tooltip';
import { isIncompleteCheckout } from 'features/subscription/utils/resolve-plan-select-action';
import {
  sidebarFooterPillClass,
  sidebarFooterPillIconColorClass,
  sidebarFooterPillLabelClass,
  SIDEBAR_FOOTER_RIGHT_ICON_SIZE,
} from 'components/blocks/layout/sidebar-footer-pill';
import { useGetOrganizationQuery } from 'features/organization';
import { getRoleTranslationKey, ROLES } from 'constant/roles';
import { getUserFullName } from 'utils/user-display-name';
import { ProfileMenuHeader } from './profile-menu-header';
import { Coins } from 'iconoir-react';
import { CreditBalanceCount } from 'features/credits/components/credit-balance-count';

/**
 * UProfileMenu Component
 *
 * A user profile dropdown menu component that displays user information and provides
 * navigation and account management options.
 *
 * Features:
 * - Displays user profile image and name
 * - Shows loading states with skeleton placeholders
 * - Provides navigation to profile page
 * - Includes theme toggling functionality
 * - Handles user logout with authentication state management
 * - Responsive design with different spacing for mobile and desktop
 *
 * Dependencies:
 * - Requires useTheme hook for theme management
 * - Requires useAuthStore for authentication state management
 * - Requires useSignoutMutation for API logout functionality
 * - Requires useGetAccount for fetching user account data
 * - Uses DropdownMenu components for the menu interface
 * - Uses React Router's useNavigate for navigation
 *
 * @returns {JSX.Element} A dropdown menu component for user profile management
 *
 * @example
 * // Basic usage in a header or navigation component
 * <UProfileMenu />
 */

const profileMenuTextClass =
  'text-sm leading-[22px] font-normal text-[#3D3D3D] dark:text-[#ffffff]';
const profileMenuSubItemClass = '!text-sm !leading-[22px] font-normal';
const profileDropdownItemClass = 'w-full rounded-none px-4 gap-4 min-h-[34px] py-[6px]';

export const UProfileMenu = ({
  isDropdownIconRequired,
  isFullNameRequired,
  variant = 'avatar',
}: UProfileItem) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isLanguageExpanded, setIsLanguageExpanded] = useState(false);
  const [isAppearanceExpanded, setIsAppearanceExpanded] = useState(false);
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);
  const isSettingsDialogOpen = useSettingsDialogStore((s) => s.isOpen);
  const settingsTab = useSettingsDialogStore((s) => s.tab);
  const openSettings = useSettingsDialogStore((s) => s.openSettings);
  const closeSettings = useSettingsDialogStore((s) => s.closeSettings);
  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { theme, selectedTheme, setTheme } = useTheme();

  // Contact sales integration
  const { mutateAsync: submitContactForm } = useContactSales();
  const { configurationSettings } = useConfig();
  const { currentLanguage, setLanguage, availableLanguages } = useLanguageContext();
  const { siteConfigurationSettings } = useSecureConfigStore();

  // Check if subscription info should be shown (only show if explicitly true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showSubscriptionInfo =
    siteConfigurationSettings?.siteConfigurations?.data?.showSubscriptionInfo === true;

  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const { mutateAsync } = useSignoutMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const user = useCommonStore((state) => state.user);
  // Every Klax plan is user-wise: the badge is the seat this person holds, not an organization plan.
  const { place: myPlace } = useMyPlan();
  const { isFreeAccess } = useFreeAccess();
  const planName = myPlace?.planName || myPlace?.planCode;

  const activeOrganizationId = selectedOrgId || user?.organizationId || '';
  const { data: organizationData } = useGetOrganizationQuery(activeOrganizationId);

  const signoutHandler = async () => {
    try {
      const res = await mutateAsync();
      if (res.isSuccess) {
        completeLogout(queryClient, navigate);
      }
    } catch (_error) {
      /* empty */
    }
  };

  const handleContactSubmit = async (data: any) => {
    const payload = {
      language: currentLanguage,
      message: data?.message,
      organizationName: data?.company,
      contactName: data?.name,
      phone: data?.phone,
      emailTo: configurationSettings.paths.salesContactMailTo,
      email: data?.email,
    };

    return await submitContactForm(payload);
  };

  const changeLanguage = async (newLanguageCode: string) => {
    if (user) {
      updateCurrentLanguage(user.id, newLanguageCode, configurationSettings);
    }
    await setLanguage(newLanguageCode);
  };

  const profileName = getUserFullName(user);
  const fullName = profileName ?? t('USER', 'User');
  const planBadgeLabel = planName
    ? planName.toLowerCase().includes('plan')
      ? planName
      : `${planName}`
    : t('NO_PLAN', 'No plan');
  const isUnpaidPlan = (value?: string | null) => {
    const key = (value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
    return key === '' || key === 'noplan' || key === 'free' || key === 'freetier';
  };
  const hasPaidPlan =
    Boolean(myPlace) && (!isUnpaidPlan(myPlace?.planName) || !isUnpaidPlan(myPlace?.planCode));
  const isPendingCheckout = isIncompleteCheckout(myPlace?.status);
  const planBadgeClassName = cn(
    'shrink-0 inline-flex items-center gap-1 rounded-full border px-3 py-0.5 text-[10px] font-semibold',
    isPendingCheckout
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300'
      : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300'
  );
  const planBadge = planBadgeLabel ? (
    isPendingCheckout ? (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={planBadgeClassName}>
              <TriangleAlert className="h-2.5 w-2.5 shrink-0" />
              {planBadgeLabel}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{t('PAYMENT_PENDING', 'Payment pending')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <span className={planBadgeClassName}>
        <Sparkles className="h-2.5 w-2.5 shrink-0" />
        {planBadgeLabel}
      </span>
    )
  ) : null;
  // Highest first: a super admin should read as such even when also an admin.
  const highestRole = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.USER].find((roleName) =>
    user?.roles?.includes(roleName)
  );
  const isAdmin = Boolean(user?.roles?.includes(ROLES.ADMIN));
  const role = highestRole ? t(getRoleTranslationKey(highestRole)) : t('NO_ROLE') || 'No role';
  const themeLabel =
    selectedTheme === 'system'
      ? t('SYSTEM') || 'System'
      : selectedTheme === 'dark'
        ? t('DARK') || 'Dark'
        : t('LIGHT') || 'Light';
  const ThemeIcon = selectedTheme === 'system' ? Monitor : theme === 'dark' ? Moon : Sun;

  const resetExpandedSections = () => {
    setIsLanguageExpanded(false);
    setIsAppearanceExpanded(false);
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (!open) {
      resetExpandedSections();
    }
  };

  const handleSheetOpenChange = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      resetExpandedSections();
    }
  };

  // Settings menu entry parked — keep handler for when it is restored.
  // const handleOpenSettings = () => {
  //   openSettings();
  //   if (isMobile) {
  //     setIsSheetOpen(false);
  //   }
  // };

  const handleOpenCredits = () => {
    navigate('/plans-billing');
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleOpenUsage = () => {
    navigate('/usage');
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleOpenUpgrade = () => {
    navigate('/subscription-plans');
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleOpenTeams = () => {
    navigate('/identity-management');
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleOpenAbout = () => {
    setIsAboutDialogOpen(true);
    setIsDropdownOpen(false);
    setIsSheetOpen(false);
  };

  const handleSignOut = () => {
    if (isMobile) {
      setIsSheetOpen(false);
    }
    signoutHandler();
  };

  const crownLabel = isFreeAccess ? t('UNLIMITED_USAGE', 'Unlimited usage allowed') : planName;

  const profileAvatar = (
    <div className="cursor-pointer rounded-full hover:bg-accent transition-colors">
      <div className="relative w-7 h-7">
        <img
          src={user?.profileImageUrl || DummyProfile}
          alt="profile"
          loading="lazy"
          className="object-cover w-7 h-7 rounded-full"
        />

        {myPlace && myPlace.planCode !== PLANS.NOPLAN && myPlace.planCode !== PLANS.FREE && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'absolute -bottom-0.5 -right-0.5 rounded-full border border-border bg-true-white p-0.5 dark:border-transparent',
                    isFreeAccess ? 'shadow-[0_0_2px_2px_#C5A017]' : 'shadow-[0_0_3px_1px_#1A1A1A]'
                  )}
                  aria-label={crownLabel}
                >
                  <Crown
                    className={cn(
                      'h-2 w-2',
                      isFreeAccess ? 'text-[#C5A017] fill-[#ae8d14]' : 'text-primary fill-primary'
                    )}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">{crownLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );

  const profilePillButton = (onClick?: React.MouseEventHandler<HTMLButtonElement>) => (
    <button
      type="button"
      className={sidebarFooterPillClass}
      onClick={onClick}
      aria-label={t('PROFILE') || 'Profile'}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <img
          src={user?.profileImageUrl || DummyProfile}
          alt="profile"
          loading="lazy"
          className="h-6 w-6 shrink-0 rounded-full object-cover"
        />
        {isFullNameRequired && (
          <div className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
            <span className={cn(sidebarFooterPillLabelClass, 'truncate')}>{fullName}</span>
            {planBadge}
          </div>
        )}
      </div>
      {isDropdownIconRequired && (
        <ChevronDown
          className={cn('shrink-0 rotate-180', sidebarFooterPillIconColorClass)}
          size={SIDEBAR_FOOTER_RIGHT_ICON_SIZE}
          strokeWidth={1.75}
        />
      )}
    </button>
  );

  const profilePill = profilePillButton();

  const profileTrigger = variant === 'pill' ? profilePill : profileAvatar;

  const planEndDate = myPlace?.currentPeriodEndUtc ? formatDate(myPlace.currentPeriodEndUtc) : '';
  let planHint = t('PLAN') || 'Plan';
  if (isPendingCheckout) {
    planHint = t('PAYMENT_PENDING', 'Payment pending');
  } else if (hasPaidPlan && planEndDate && myPlace?.cancelAtPeriodEnd) {
    planHint = t('PLAN_ENDS_ON', { date: planEndDate, defaultValue: 'Ends {{date}}' });
  } else if (hasPaidPlan && planEndDate) {
    planHint = t('PLAN_RENEWS_ON', { date: planEndDate, defaultValue: 'Renews {{date}}' });
  } else if (!hasPaidPlan && isAdmin) {
    planHint = t('UPGRADE_TO_GET_STARTED');
  }

  const profileHeader = (
    <ProfileMenuHeader
      name={profileName}
      email={user?.email}
      role={role}
      organizationName={
        organizationData?.name && organizationData.name !== 'Default'
          ? organizationData.name
          : undefined
      }
      planLabel={planBadgeLabel}
      planHint={planHint}
      hasPlan={hasPaidPlan}
      isPendingCheckout={isPendingCheckout}
      pendingLabel={t('PAYMENT_PENDING', 'Payment pending')}
      onPlanClick={isAdmin ? handleOpenUpgrade : undefined}
    />
  );

  const mobileLanguageSection = (
    <div className="flex flex-col">
      <button
        type="button"
        className="bg-background hover:bg-accent flex w-full items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
        onClick={() => setIsLanguageExpanded((prev) => !prev)}
      >
        <Globe className="h-4 w-4 text-foreground shrink-0" />
        <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
          {t('LANGUAGE') || 'Language'}:{' '}
          <span
            className={`${theme === 'dark' ? 'dark:border-black-500' : 'text-primary-600'} font-medium ml-1`}
          >
            {currentLanguage.split('-')[0].toUpperCase()}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-foreground shrink-0 transition-transform',
            isLanguageExpanded && 'rotate-180'
          )}
        />
      </button>
      {isLanguageExpanded ? (
        <div className="ml-8 mt-1 flex flex-col gap-0.5">
          {availableLanguages?.map((lang) => (
            <button
              key={lang.itemId}
              type="button"
              className={cn(
                profileMenuTextClass,
                'px-3 py-[6px] cursor-pointer rounded-[6px] text-left hover:bg-accent transition-colors',
                lang.languageCode === currentLanguage
                  ? 'text-primary-600'
                  : 'text-typography-grey-1-secondary'
              )}
              onClick={() => changeLanguage(lang.languageCode)}
            >
              {getLanguageMenuLabel(lang, availableLanguages)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  const mobileAppearanceSection = (
    <div className="flex flex-col">
      <button
        type="button"
        className="bg-background hover:bg-accent flex w-full items-center px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
        onClick={() => setIsAppearanceExpanded((prev) => !prev)}
      >
        <Palette className="h-4 w-4 text-foreground mr-4 shrink-0" />
        <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
          {t('APPEARANCE') || 'Appearance'}:{' '}
          <span
            className={`${theme === 'dark' ? 'dark:border-black-500' : 'text-primary-600'} font-medium mr-1 capitalize truncate`}
          >
            {themeLabel}
          </span>
        </span>
        <ThemeIcon
          className={`h-4 w-4 shrink-0 ${theme === 'dark' ? 'dark:border-black-500' : 'text-primary-600'}`}
        />
        <ChevronDown
          className={cn(
            'h-4 w-4 text-foreground shrink-0 ml-2 transition-transform',
            isAppearanceExpanded && 'rotate-180'
          )}
        />
      </button>
      {isAppearanceExpanded ? (
        <div className="ml-8 mt-1 flex flex-col gap-0.5">
          <button
            type="button"
            className={cn(
              profileMenuTextClass,
              'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] hover:bg-accent transition-colors',
              selectedTheme === 'system' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
            )}
            onClick={() => setTheme('system')}
          >
            <span>{t('SYSTEM') || 'System'}</span>
            <Monitor className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(
              profileMenuTextClass,
              'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] hover:bg-accent transition-colors',
              selectedTheme === 'dark' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
            )}
            onClick={() => setTheme('dark')}
          >
            <span>{t('DARK') || 'Dark'}</span>
            <Moon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(
              profileMenuTextClass,
              'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] hover:bg-accent transition-colors',
              selectedTheme === 'light' ? 'text-primary-600' : 'text-typography-grey-1-secondary'
            )}
            onClick={() => setTheme('light')}
          >
            <span>{t('LIGHT') || 'Light'}</span>
            <Sun className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );

  const mobileMenuContent = (
    <div className="flex flex-col h-full pt-3 pb-[14px]">
      {profileHeader}

      <div className="flex flex-col mt-[14px] mx-2 pb-[14px] border-b-[0.5px] border-border">
        {/* Settings entry parked — Account page is the replacement surface.
        <button
          type="button"
          className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
          onClick={handleOpenSettings}
        >
          <Settings className="h-4 w-4 text-foreground" />
          <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
            {t('SETTINGS') || 'Settings'}
          </span>
        </button>
        */}
        <button
          type="button"
          className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
          onClick={handleOpenUsage}
        >
          <Gauge className="h-4 w-4 text-foreground" />
          <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
            {t('USAGE') || 'Usage'}
          </span>
        </button>
        {isAdmin && (
          <button
            type="button"
            className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
            onClick={handleOpenCredits}
          >
            <Coins className="h-4 w-4 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
              {t('CREDITS', 'Credits')}
            </span>
            <CreditBalanceCount />
          </button>
        )}
        {isAdmin && (
          <button
            type="button"
            className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
            onClick={handleOpenTeams}
          >
            <UserCog className="h-4 w-4 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
              {t('TEAMS', { defaultValue: 'Teams' })}
            </span>
          </button>
        )}
        {mobileLanguageSection}
        {mobileAppearanceSection}
      </div>

      <div className="mx-4 my-0 h-px bg-border/90" />

      <div className="mx-2 my-[14px]">
        <button
          type="button"
          className="bg-background hover:bg-accent flex w-full items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
          onClick={handleOpenAbout}
        >
          <Info className="h-4 w-4 text-foreground" />
          <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
            {t('ABOUT') || 'About'}
          </span>
        </button>
      </div>

      <div className="mx-4 my-0 h-px bg-border/90" />

      <button
        type="button"
        className="bg-background hover:bg-accent flex items-center mx-2 mt-[14px] gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors"
        onClick={handleSignOut}
      >
        <LogOut className="h-4 w-4 text-foreground" />
        <span className={cn(profileMenuTextClass, 'flex-1 text-left')}>
          {t('LOG_OUT') || 'Log out'}
        </span>
      </button>
    </div>
  );

  const dialogs = (
    <>
      {isModalOpen && <ArchiveChat isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
      <KnowledgeBaseModal
        isOpen={isKnowledgeBaseModalOpen}
        onClose={() => setIsKnowledgeBaseModalOpen(false)}
      />
      <Dialog open={isAboutDialogOpen} onOpenChange={setIsAboutDialogOpen}>
        <DialogContent className="max-w-[360px] gap-0 border-0 bg-background p-0 shadow-2xl">
          <div className="flex flex-col items-center gap-6 px-8 py-10">
            <div className="flex flex-col items-center gap-2 text-center">
              <h2 className="font-heyJune text-3xl font-normal leading-none tracking-tight text-foreground">
                klax!
              </h2>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t('VERSION', 'Version')} {__APP_VERSION__} ({__BUILD_SHA__})
              </p>
            </div>
          </div>

          <div className="border-t border-border/50 p-6">
            <Button
              variant="ghost"
              className="w-full font-medium text-foreground hover:bg-accent/50"
              onClick={() => setIsAboutDialogOpen(false)}
            >
              {t('CLOSE') || 'Close'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SettingsDialog
        isOpen={isSettingsDialogOpen}
        setIsOpen={(open) => (open ? openSettings(settingsTab) : closeSettings())}
        initialTab={settingsTab}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSubmit={handleContactSubmit}
        title={t('CONTACT_US')}
        initialData={{
          name: fullName,
          email: user?.email || '',
          phone: user?.phoneNumber || '',
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className={variant === 'pill' ? 'w-full' : undefined}>
        {variant === 'pill' ? (
          profilePillButton(() => setIsSheetOpen(true))
        ) : (
          <button
            type="button"
            onClick={() => setIsSheetOpen(true)}
            aria-label={t('PROFILE') || 'Profile'}
          >
            {profileAvatar}
          </button>
        )}
        <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
          <SheetContent
            side="right"
            className="w-[280px] sm:max-w-[280px] p-0 border-border shadow-primary-soft"
          >
            {mobileMenuContent}
          </SheetContent>
        </Sheet>
        {dialogs}
      </div>
    );
  }

  return (
    <div className={variant === 'pill' ? 'w-full min-w-0' : undefined}>
      <DropdownMenu open={isDropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <DropdownMenuTrigger asChild className={variant === 'pill' ? 'w-full' : undefined}>
          {profileTrigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="flex w-[340px] flex-col items-stretch rounded-[8px] border-[0.5px] bg-background p-0 pb-[14px] pt-3 shadow-primary-soft"
          align={variant === 'pill' ? 'start' : 'end'}
          side="top"
          sideOffset={10}
        >
          {profileHeader}

          {/* Group 1: Settings, Language, Appearance */}
          <div className="mt-[14px] flex w-full flex-col border-b border-border/90 pb-[14px]">
            {/* Settings entry parked — Account page is the replacement surface.
            <DropdownMenuItem className={profileDropdownItemClass} onClick={handleOpenSettings}>
              <Settings className="h-4 w-4 shrink-0 text-foreground" />
              <span className={cn(profileMenuTextClass, 'flex-1')}>
                {t('SETTINGS') || 'Settings'}
              </span>
            </DropdownMenuItem>
            */}

            <DropdownMenuItem className={profileDropdownItemClass} onClick={handleOpenUsage}>
              <Gauge className="h-4 w-4 shrink-0 text-foreground" />
              <span className={cn(profileMenuTextClass, 'flex-1')}>{t('USAGE') || 'Usage'}</span>
            </DropdownMenuItem>

            {isAdmin && (
              <DropdownMenuItem className={profileDropdownItemClass} onClick={handleOpenCredits}>
                <Coins className="h-4 w-4 shrink-0 text-foreground" />
                <span className={cn(profileMenuTextClass, 'flex-1')}>
                  {t('CREDITS', 'Credits')}
                </span>
                <CreditBalanceCount />
              </DropdownMenuItem>
            )}

            {isAdmin && (
              <DropdownMenuItem className={profileDropdownItemClass} onClick={handleOpenTeams}>
                <UserCog className="h-4 w-4 shrink-0 text-foreground" />
                <span className={cn(profileMenuTextClass, 'flex-1')}>
                  {t('TEAMS', { defaultValue: 'Teams' })}
                </span>
              </DropdownMenuItem>
            )}

            {/* Appearance Sub-menu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className={profileDropdownItemClass}>
                <Palette className="h-4 w-4 shrink-0 text-foreground" />
                <span
                  className={cn(profileMenuTextClass, 'flex min-w-0 flex-1 items-center gap-1')}
                >
                  <span className="whitespace-nowrap">{t('APPEARANCE') || 'Appearance'}:</span>
                  <span data-testid="appearance-theme-label" className="min-w-0 flex-1">
                    <TruncateTooltip
                      text={themeLabel}
                      className={cn(
                        'font-medium capitalize',
                        theme === 'dark' ? 'text-true-white' : 'text-grey-900'
                      )}
                    />
                  </span>
                  <ThemeIcon
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0',
                      theme === 'dark' ? 'text-true-white' : 'text-primary-600'
                    )}
                  />
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                className="bg-background border border-border p-1.5 rounded-[12px] min-w-[140px] shadow-xl"
                sideOffset={12}
              >
                <DropdownMenuItem
                  className={cn(
                    profileMenuSubItemClass,
                    'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-accent',
                    selectedTheme === 'system'
                      ? 'text-primary-600'
                      : 'text-typography-grey-1-secondary'
                  )}
                  onClick={() => setTheme('system')}
                >
                  <span>{t('SYSTEM') || 'System'}</span>
                  <Monitor className="h-4 w-4" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={cn(
                    profileMenuSubItemClass,
                    'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-accent',
                    selectedTheme === 'dark'
                      ? 'text-primary-600'
                      : 'text-typography-grey-1-secondary'
                  )}
                  onClick={() => setTheme('dark')}
                >
                  <span>{t('DARK') || 'Dark'}</span>
                  <Moon className="h-4 w-4" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={cn(
                    profileMenuSubItemClass,
                    'flex items-center justify-between px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-accent',
                    selectedTheme === 'light'
                      ? 'text-primary-600'
                      : 'text-typography-grey-1-secondary'
                  )}
                  onClick={() => setTheme('light')}
                >
                  <span>{t('LIGHT') || 'Light'}</span>
                  <Sun className="h-4 w-4" />
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </div>

          <DropdownMenuSeparator className="my-0 bg-border/90" />

          {/* Group 3: About */}
          <DropdownMenuItem
            className={cn(profileDropdownItemClass, 'my-[14px]')}
            onClick={handleOpenAbout}
          >
            <Info className="h-4 w-4 shrink-0 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1')}>{t('ABOUT') || 'About'}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-0 bg-border/90" />

          {/* Group 4: Log out */}
          <DropdownMenuItem
            className={cn(profileDropdownItemClass, 'mt-[14px]')}
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4 shrink-0 text-foreground" />
            <span className={cn(profileMenuTextClass, 'flex-1')}>{t('LOG_OUT') || 'Log out'}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </div>
  );
};
